import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { v4 } from 'uuid';
import { IAlert, IAlertTypes } from './alerts.interfaces';
import { PartialBy } from '@shared/types';
import { Capacitor } from '@capacitor/core';

const DEFAULT_ALERT_DURATION = 5000;

@Injectable({ providedIn: 'root' })
export class AlertsService {
  showAlert$: Observable<IAlert | null>;

  private alertQueue: IAlert[] = [];
  private readonly showAlertSource$ = new Subject<IAlert | null>();

  constructor() {
    this.showAlert$ = this.showAlertSource$.asObservable();
  }

  get alertDuration(): number {
    return DEFAULT_ALERT_DURATION;
  }

  get queue(): IAlert[] {
    return this.alertQueue;
  }

  getTextMessage(partyName: string, partySize: number, partyLabel: string, customerLabel: string): string {
    return `${partyName} ${customerLabel || 'customer'} of ${partySize}`;
  }

  clear(): void {
    this.alertQueue = [];
    this.showAlertSource$.next(null);
  }

  add(newAlert: PartialBy<IAlert, 'id'>): string {
    const alert: IAlert = {
      ...newAlert,
      id: newAlert.id ? newAlert.id : v4(),
      local: newAlert.local ? newAlert.local : false,
    };

    if (this.alertQueue.findIndex(item => item.id === alert.id) !== -1) {
      return alert.id;
    }

    this.showAlertSource$.next(alert);
    this.alertQueue.push(alert);

    return alert.id;
  }

  addWarning(error: string, heading = 'Error:', duration = this.alertDuration): void {
    this.add({
      type: IAlertTypes.warning,
      duration,
      heading,
      body: error,
    });
  }

  addError(
    initialError: string,
    heading = 'Error:',
    duration = this.alertDuration,
    isManager = false,
  ): void {
    let error = initialError;

    if (error.includes('ERROR_TRANSACTION_DECLINED')) {
      const contactWithAdminText = isManager ? ' Contact your account administrator for this matter.' : '';

      error = `
        Your transaction was declined.
        Please use a different card or contact your bank.
        Update your card on the Billing page.
        ${contactWithAdminText}
      `
    }

    if (error === 'ERROR_RECURRENT_RETRY') {
      error = Capacitor.isNativePlatform()
        ? 'Please wait 60 seconds and try again.'
        : `This account has multiple subscriptions to the same plan.
        To avoid accidental duplicate subscriptions or changes, a short delay is required.
        Please wait 60 seconds and then try again.`;
    }

    this.add({
      type: IAlertTypes.danger,
      duration,
      heading,
      body: error,
    });
  }

  addSessionExpiredError(error: string, heading = 'Error:', duration = this.alertDuration): void {
    this.add({
      id: 'session-expired',
      type: IAlertTypes.danger,
      duration,
      heading,
      body: error,
    });
  }

  addSuccess(body: string, heading = 'Updated:', duration = this.alertDuration): void {
    this.add({
      type: IAlertTypes.success,
      duration,
      heading,
      body,
    });
  }

  addInfo(body: string, heading = 'Info:', duration = this.alertDuration): void {
    this.add({
      type: IAlertTypes.info,
      duration,
      heading,
      body,
    });
  }

  dismissById(id: string): void {
    this.alertQueue = this.alertQueue.filter(item => item.id !== id);
  }
}
